import React, { useEffect } from "react";
import { Button, Collapse, Row, Col } from "antd";
import { CopyOutlined } from "@ant-design/icons";
import OfferSelectionTable from "./OfferSelectionTable";
import ItemEstimationAndStock from "./ItemEstimationAndStock";
import ActionsBlock from "./ActionsBlock";
import { predictStockForVariations } from "../../../core/utilities/functions";
import { getSuppliersByType } from "../../../core/api/settings";
import dayjs from "dayjs";
import { isEqual } from "lodash";
import CampaignsPanelInput from "./CampaignsPanelInput";

const { Panel } = Collapse;

const CampaignsPanel = ({
  campaigns,
  onDuplicate,
  onChange,
  onSave,
  onDelete,
  strategyOptions,
  statusOptions,
  brandOptions,
  disabled,
  applyAction,
  handleBrandChange,
  handleProductChange,
  mode, // Add mode prop to control field editability and button visibility
  onViewOfferDetails, // Add handler for viewing offer details
}) => {

  
  useEffect(() => {
    campaigns.forEach((campaign, idx) => {
      const { offer, mailQty, rateOfResponse } = campaign;
      if (!offer || mailQty == null || rateOfResponse == null) return;
  
      const estimations = [];
      const seenVariationIds = new Set();
      // Remove the code that adds previous itemEstimation entries to ensure complete replacement
      offer.marketing_skus.forEach(ms => {
        (ms.marketing_dependent_skus_details || []).forEach(detail => {
          const varId = detail.product_variation;
          if (
            detail.product_type.toUpperCase().includes("DELIVERY")|| detail.product_type.toUpperCase().includes("DISCOUNT") ||
            seenVariationIds.has(varId)
          ) return;
  
          let qtyPerOrder = 5.5;
          if (
            detail.product_type.toUpperCase().includes("ACCESSORY")
          ) qtyPerOrder = 1;
          else {
            qtyPerOrder=5.5
          }
          const totalQty   = +(mailQty * rateOfResponse * qtyPerOrder * 0.01).toFixed(3);
  
          estimations.push({
            offer_item_id: detail.id,
            offer_item_description: detail.product_variation_desc,
            qtyPerOrder,
            totalQty,
            product_variation_id:   varId,
          });
          seenVariationIds.add(varId);
        });
      });
  
      (offer.product_variations_detail || []).forEach(variation => {
        const varId = variation.id;
        if (
          variation.product_type.toUpperCase().includes("DELIVERY")|| variation.product_type.toUpperCase().includes("DISCOUNT") ||
          seenVariationIds.has(varId)
        ) return;
  
        let qtyPerOrder = 5.5;
        if (
          variation.product_type.toUpperCase().includes("ACCESSORY")
        ) qtyPerOrder = 1;
        else qtyPerOrder = 5.5
        const totalQty   = +(mailQty * rateOfResponse * qtyPerOrder * 0.01).toFixed(3);
  
        estimations.push({
          offer_item_id:          variation.id,
          offer_item_description: variation.description,
          qtyPerOrder,
          totalQty,
          product_variation_id:   varId,
        });
        seenVariationIds.add(varId);
      });
      onChange(idx, "itemEstimation", estimations);
    });
    
  }, [
    campaigns.map(c => c.offer?.id).join(","),
    campaigns.map(c => c.mailQty).join(","),
    campaigns.map(c => c.rateOfResponse).join(","),
  ]);

  async function calculateStockOnMailDate(
    itemsEstimation,
    mailDate,
    clientIds = []
  ) {
    if (!itemsEstimation?.length || !mailDate || !clientIds.length) {
      return [];
    }
  
    const variationIds = [
      ...new Set(
        itemsEstimation.map((item) => item.product_variation_id)
      ),
    ];
  
    const stockResults = await predictStockForVariations(
      dayjs(mailDate),
      clientIds,
      variationIds
    );
  
    return itemsEstimation.map((item) => {
      const variationId = item.product_variation_id;
      const stock = stockResults[variationId] ?? 0;
      return {
        variation: item.offer_item_description,
        stock,
        needed: Math.ceil(item.totalQty) || 0,
      };
    });
  }

  useEffect(() => {
    campaigns.forEach((campaign, idx) => {
      const { itemEstimation, mailDate, clientIds, mailStockData } = campaign;
      if (!itemEstimation?.length || !mailDate || !clientIds?.length) return;
  
      calculateStockOnMailDate(itemEstimation, mailDate, clientIds)
        .then(stockData => {
          // only update if stockData is actually different
          if (!isEqual(stockData, mailStockData)) {
            onChange(idx, 'mailStockData', stockData);
          }
        })
        .catch(console.error);
    });
  }, [
    campaigns.map(c =>
      [
        c.mailDate,
        c.clientIds?.join(',') ?? '',
        c.itemEstimation?.map(i => i.product_variation_id).join(',') ?? '',
        JSON.stringify(c.mailStockData),
      ].join('|')
    ).join(';'),
  ]);
  
  // Fetch supplier options when brand changes
  useEffect(() => {
    const fetchSupplierOptions = async (campaign, idx) => {
      if (!campaign.brand) return;
      
      try {
        // Fetch suppliers for each type
        const [printerRes, lettershopRes, postageRes, dataProcessingRes] = await Promise.all([
          getSuppliersByType("Printer", campaign.brand),
          getSuppliersByType("Lettershop", campaign.brand),
          getSuppliersByType("Postage", campaign.brand),
          getSuppliersByType("Data Processing", campaign.brand)
        ]);
        
        // Format options for select components
        const printerOptions = printerRes.data.map(p => ({ value: p.id, label: p.name }));
        const lettershopOptions = lettershopRes.data.map(p => ({ value: p.id, label: p.name }));
        const postageOptions = postageRes.data.map(p => ({ value: p.id, label: p.name }));
        const dataProcessingOptions = dataProcessingRes.data.map(p => ({ value: p.id, label: p.name }));
        
        // Update campaign with options
        onChange(idx, 'printerOptions', printerOptions);
        onChange(idx, 'lettershopOptions', lettershopOptions);
        onChange(idx, 'postageOptions', postageOptions);
        onChange(idx, 'dataProcessingOptions', dataProcessingOptions);
        
        // Auto-select if there's only one option available
        if (printerOptions.length === 1 && !campaign.printer) {
          onChange(idx, 'printer', printerOptions[0].value);
        }
        
        if (lettershopOptions.length === 1 && !campaign.lettershop) {
          onChange(idx, 'lettershop', lettershopOptions[0].value);
        }
        
        if (postageOptions.length === 1 && !campaign.postage) {
          onChange(idx, 'postage', postageOptions[0].value);
        }
        
        if (dataProcessingOptions.length === 1 && !campaign.dataProcessing) {
          onChange(idx, 'dataProcessing', dataProcessingOptions[0].value);
        }
      } catch (error) {
        console.error("Error fetching supplier options:", error);
      }
    };
    
    campaigns.forEach((campaign, idx) => {
      fetchSupplierOptions(campaign, idx);
    });
  }, [campaigns.map(c => c.brand).join(',')]);
  
  
  return (
  <Collapse accordion bordered={false} style={{ background: "none" }} defaultActiveKey={0}>
    {campaigns.map((campaign, idx) => (
      <Panel
        key={idx}
        header={
          <Row align="middle" justify="space-between" style={{ width: "100%" }}>
            <Col flex="auto">
              <b>
                Campaign {idx + 1}: {campaign.code}
              </b>
            </Col>
            <Col>
              {mode !== 'view' && (
                <Button
                  icon={<CopyOutlined />}
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDuplicate(idx);
                  }}
                  style={{ marginLeft: 8 }}
                  disabled={disabled}
                >
                  Duplicate
                </Button>
              )}
            </Col>
          </Row>
        }
      >

        <CampaignsPanelInput
          onChange={onChange}
          campaign={campaign}
          brandOptions={brandOptions}
          idx={idx}
          handleBrandChange={(val) => handleBrandChange(val, idx)}
          handleProductChange={(val) => handleProductChange(val, idx)}
          productOptions={campaign.productOptions}
          variationOptions={campaign.variationOptions}
          printerOptions={campaign.printerOptions}
          lettershopOptions={campaign.lettershopOptions}
          postageOptions={campaign.postageOptions}
          dataProcessingOptions={campaign.dataProcessingOptions}
          mode={mode}
          strategyOptions={strategyOptions}
          statusOptions={statusOptions}
        />
 
        <Collapse bordered={false} style={{ marginBottom: 8 }}>
          <Collapse.Panel
            header={
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  width: "100%",
                }}
              >
                <span>Offer Selection</span>
                <span>Selected: {campaign?.offer?.code_offer || "None"}</span>
              </div>
            }
            key="offer-selection"
          >
            <OfferSelectionTable
              offerOptions={campaign.offerOptions}
              selectedOffer={campaign.offer}
              onSelectOffer={(offer) => onChange(idx, "offer", offer)}
              disabled={mode === 'view' || mode === 'edit'}
              onViewOfferDetails={onViewOfferDetails}
            />
          </Collapse.Panel>
        </Collapse>
        <ItemEstimationAndStock
          itemsEstimation={campaign.itemEstimation}
          mailStockData={campaign.mailStockData}
          handleQtyChange={(itemIdx, newQty) => {
            const updated = campaign.itemEstimation.map((item, i) => {
              if (i !== itemIdx) return item;
              const qtyPerOrder = newQty;
              const totalQty = +(
                campaign.mailQty *
                campaign.rateOfResponse *
                qtyPerOrder *
                0.01
              ).toFixed(3);
              return { ...item, qtyPerOrder, totalQty };
            });
            onChange(idx, "itemEstimation", updated);
          }}
          disabled={mode === 'view' || campaign.isGeneratingActions}
          panelHeaderExtra={
            <span style={{ fontWeight: 500 }}>
              Number of Items:{" "}
              <span style={{ color: "#1890ff" }}>
                {campaign.itemEstimation?.length || 0}
              </span>
            </span>
          }
        />

        <ActionsBlock
          isGeneratingActions={campaign.isGeneratingActions}
          suggestedActions={campaign.suggestedActions}
          itemEstimation={campaign.itemEstimation}
          applyAction={(action, offerItemDescription) =>
            applyAction(idx, action, offerItemDescription)
          }
          disabled={mode === 'view' || campaign.isGeneratingActions}
          panelHeaderExtra={(() => {
            const totalActions =
              campaign.itemEstimation?.reduce((sum, item) => {
                const actions =
                  campaign.suggestedActions?.[item.offer_item_description] ||
                  [];
                return sum + actions.length;
              }, 0) || 0;
            return (
              <span style={{ fontWeight: 500 }}>
                Number of Actions:{" "}
                <span
                  style={{ color: totalActions > 0 ? "#ff4d4f" : "#52c41a" }}
                >
                  {totalActions}
                </span>
              </span>
            );
          })()}
        /> 
        <Row justify="end" gutter={8}>
          <Col>
            {mode !== 'view' && (
              <Button
                type="primary"
                onClick={() => onSave(idx)}
                disabled={disabled}
              >
                {campaign.id ? 'Update Campaign' : 'Save Campaign'}
              </Button>
            )}
          </Col>
          <Col hidden = {campaigns.length===1}>
            {mode !== 'view' && (
              <Button danger onClick={() => onDelete(idx)} disabled={disabled}>
                Delete
              </Button>
            )}
          </Col>
        </Row>
      </Panel>
    ))}
  </Collapse>
)};

export default CampaignsPanel;
